/**
 * Theme toggle component
 */

import { Moon, Sun, Monitor } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { cn } from '../../lib/utils';

interface ThemeToggleProps {
  variant?: 'icon' | 'dropdown';
  className?: string;
}

export default function ThemeToggle({ variant = 'icon', className }: ThemeToggleProps) {
  const { theme, setTheme, toggleTheme, isDark } = useTheme();

  if (variant === 'icon') {
    return (
      <button
        onClick={toggleTheme}
        className={cn(
          'p-2.5 rounded-xl transition-all duration-200',
          'text-neutral-600 dark:text-neutral-400',
          'hover:bg-neutral-100 dark:hover:bg-neutral-800',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          'active:scale-95',
          className
        )}
        aria-label={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}
      >
        {isDark ? (
          <Sun className="w-5 h-5 transition-transform duration-300 rotate-0" />
        ) : (
          <Moon className="w-5 h-5 transition-transform duration-300 rotate-0" />
        )}
      </button>
    );
  }

  return (
    <div className={cn('flex items-center gap-1 p-1 rounded-xl bg-neutral-100 dark:bg-neutral-800', className)}>
      <ThemeButton
        active={theme === 'light'}
        onClick={() => setTheme('light')}
        label="Claro"
      >
        <Sun className="w-4 h-4" />
      </ThemeButton>
      <ThemeButton
        active={theme === 'dark'}
        onClick={() => setTheme('dark')}
        label="Escuro"
      >
        <Moon className="w-4 h-4" />
      </ThemeButton>
      <ThemeButton
        active={theme === 'system'}
        onClick={() => setTheme('system')}
        label="Sistema"
      >
        <Monitor className="w-4 h-4" />
      </ThemeButton>
    </div>
  );
}

function ThemeButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'p-2 rounded-lg transition-all duration-200',
        active
          ? 'bg-white dark:bg-neutral-700 shadow-sm text-primary-600 dark:text-primary-400'
          : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
      )}
      aria-label={label}
    >
      {children}
    </button>
  );
}
